import { ReactNode } from 'react'
import { motion, HTMLMotionProps } from 'framer-motion'
import { clsx } from 'clsx'

interface CardProps extends HTMLMotionProps<'div'> {
  children: ReactNode
  variant?: 'default' | 'glass' | 'solid'
  hover?: boolean
  className?: string
}

export function Card({
  children,
  variant = 'glass',
  hover = false,
  className,
  ...props
}: CardProps) {
  const baseStyles = 'rounded-2xl p-6'

  const variants = {
    default: 'bg-gray-50 dark:bg-dark-bg-secondary border border-gray-200 dark:border-white/5',
    glass: 'glass-card',
    solid: 'bg-gray-100 dark:bg-dark-bg-tertiary border border-gray-300 dark:border-white/10',
  }

  const hoverStyles = hover
    ? 'transition-all duration-300 hover:border-accent-cyan/30 hover:shadow-lg hover:shadow-accent-cyan/5'
    : ''

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={clsx(baseStyles, variants[variant], hoverStyles, className)}
      {...props}
    >
      {children}
    </motion.div>
  )
}

export function CardHeader({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={clsx('mb-4 pb-4 border-b border-gray-200 dark:border-white/5', className)}>
      {children}
    </div>
  )
}

export function CardTitle({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <h3 className={clsx('text-lg font-semibold text-gray-900 dark:text-white', className)}>
      {children}
    </h3>
  )
}

export function CardDescription({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <p className={clsx('text-sm text-gray-600 dark:text-gray-400 mt-1', className)}>{children}</p>
  )
}

export function CardContent({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={clsx(className)}>{children}</div>
}
